/**
 * Timeline view with colored thread lanes for shipment events.
 *
 * Top N email threads get assigned colored vertical lanes for visual tracking.
 * Other threads are grouped in a gray "other" lane. Messages are displayed
 * as bubbles with sender tabs, content previews, and attachments.
 *
 * Features:
 * - Sticky headers showing current thread names
 * - Visual lane continuity (solid/dashed lines)
 * - Search and thread filtering
 * - Attachment downloads
 * - Email thread dialog on click
 *
 * @example
 * <ShipmentTimelineWithThreadLines
 *   shipmentId="abc123"
 *   numberOfLanes={4}
 *   laneThickness={10}
 * />
 */

import React, { useState, useMemo, useRef, useEffect } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Collapse,
  Link,
  Divider,
} from "@mui/material";
import WarningIcon from "@mui/icons-material/Warning";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useSelector } from "react-redux";
import { RootState } from "@/store/rootReducer";
import { EventLogger } from "@/utils/eventLogger";
import { apiClient } from "../../utils/apiClient";
import type {
  DownloadAttachmentRequest,
  DownloadAttachmentResponse,
} from "../../../../src/shipments/types";
import { ShipmentDetails } from "../ShipmentDetails";
import { ShipmentTimelineRemindersComponent } from "../ShipmentTimelineRemindersComponent";
import DobbyChatWrapper from "../../components/Chat/DobbyChatWrapper";
import { EmailThreadComponent } from "../../components/EmailThreadComponent";

import type { ShipmentTimelineWithThreadLinesProps } from "./types";
import {
  DEFAULT_LANE_COLORS,
  DEFAULT_OTHER_THREADS_LANE_COLOR,
} from "./constants";
import {
  useShipmentData,
  useThreadCalculations,
  useStickyHeaders,
  useLanePositions,
} from "./hooks";
import {
  EmailDialog,
  DebugMenu,
  TimelineFilters,
  StickyThreadHeader,
  LaneSegments,
  TimelineMessage,
} from "./components";

// Shared styles for collapsible section headers
const sectionHeaderSx = {
  display: "flex",
  alignItems: "center",
  gap: 1,
  cursor: "pointer",
  py: 1,
  px: 1,
  mx: -1,
  borderRadius: 1,
  transition: "background-color 0.15s ease",
  "&:hover": {
    backgroundColor: "action.hover",
  },
};

export function ShipmentTimelineWithThreadLines({
  shipmentId,
  updates,
  emailSource,
  includeInternalReminders = false,
  numberOfLanes = 4,
  laneThickness = 10,
  laneSpacing = 40,
  lanesLeftOffset = 10,
  bubbleWidth = 50,
  bubbleBaseOffset = 0,
  bubbleReplyOffsetPercent = 0,
  dashedLineLength = 16,
  bottomPadding = 68,
  dateColumnWidth = 50,
  laneColors = DEFAULT_LANE_COLORS,
  otherThreadsLaneColor = DEFAULT_OTHER_THREADS_LANE_COLOR,
}: ShipmentTimelineWithThreadLinesProps) {
  // Data fetching
  const { events, shipment, loading, error, refetch } = useShipmentData(
    shipmentId,
    includeInternalReminders,
  );

  // Filter state
  const [openEmailId, setOpenEmailId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [showOnlyWithDocuments, setShowOnlyWithDocuments] = useState(false);
  const [selectedThreadIds, setSelectedThreadIds] = useState<string[]>([]);

  // Collapse state for all sections (only Timeline expanded by default)
  const [remindersExpanded, setRemindersExpanded] = useState(false);
  const [detailsExpanded, setDetailsExpanded] = useState(false);
  const [timelineExpanded, setTimelineExpanded] = useState(true);

  // Highlight state for scrolling to email from chat citation
  const [highlightedEmailId, setHighlightedEmailId] = useState<string | null>(
    null
  );

  // Refs for tracking
  const timelineContainerRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const messageRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  // Get user data from Redux store
  const user = useSelector((state: RootState) => state.userData.user);

  // Filter and sort events chronologically
  const filteredAndSortedEvents = useMemo(() => {
    let filtered = events;

    // Get thread info for filtering
    const threadMap = new Map<string, { count: number }>();
    events.forEach((event) => {
      if (event.threadId) {
        const existing = threadMap.get(event.threadId);
        if (existing) {
          existing.count++;
        } else {
          threadMap.set(event.threadId, { count: 1 });
        }
      }
    });
    const totalThreads = threadMap.size;

    // Apply thread filter (only skip filtering when ALL threads are selected)
    if (totalThreads > 0 && selectedThreadIds.length < totalThreads) {
      filtered = filtered.filter(
        (event) => event.threadId && selectedThreadIds.includes(event.threadId),
      );
    }

    // Apply document filter
    if (showOnlyWithDocuments) {
      filtered = filtered.filter(
        (event) => event.attachments && event.attachments.length > 0,
      );
    }

    // Apply search query filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();

      filtered = filtered.filter((event) => {
        if (event.name?.toLowerCase().includes(query)) return true;
        if (event.emailSubject?.toLowerCase().includes(query)) return true;
        if (event.snippet?.toLowerCase().includes(query)) return true;
        if (event.preview?.toLowerCase().includes(query)) return true;
        if (event.context?.toLowerCase().includes(query)) return true;
        if (
          event.categories
            ?.join(" ")
            .replace(/_/g, " ")
            .toLowerCase()
            .includes(query)
        )
          return true;
        if (
          event.attachments?.some((att: { description?: string }) =>
            att.description?.toLowerCase().includes(query),
          )
        )
          return true;
        return false;
      });
    }

    // Sort by date (oldest first, so most recent appears at bottom like iMessage)
    filtered.sort((a, b) => {
      if (a.date && b.date) {
        return new Date(a.date).getTime() - new Date(b.date).getTime();
      }
      return 0;
    });

    return filtered;
  }, [events, searchQuery, showOnlyWithDocuments, selectedThreadIds]);

  // Thread calculations
  const {
    threadInfo,
    laneThreads,
    getLaneInfo,
    threadMessageIndices,
    otherThreadSegments,
  } = useThreadCalculations(
    events,
    filteredAndSortedEvents,
    numberOfLanes,
    laneColors,
  );

  // Lane positions
  const { messageLanePositions, updateLanePositions } = useLanePositions({
    messagesContainerRef,
    messageRefs,
    events: filteredAndSortedEvents,
  });

  // Sticky headers
  const { stickyLaneThreads, stickyOtherThreadSegment } = useStickyHeaders({
    containerRef: timelineContainerRef,
    messageRefs,
    laneThreads,
    threadMessageIndices,
    otherThreadSegments,
    updateLanePositions,
  });

  // Initialize selected threads when events load
  useEffect(() => {
    if (threadInfo.length > 0 && selectedThreadIds.length === 0) {
      setSelectedThreadIds(threadInfo.map((t) => t.threadId));
    }
  }, [threadInfo]); // eslint-disable-line react-hooks/exhaustive-deps

  // Scroll to bottom (most recent messages) when events load - like iMessage
  useEffect(() => {
    if (filteredAndSortedEvents.length > 0 && timelineContainerRef.current) {
      // Small delay to ensure DOM is rendered
      setTimeout(() => {
        if (timelineContainerRef.current) {
          timelineContainerRef.current.scrollTop =
            timelineContainerRef.current.scrollHeight;
        }
      }, 150);
    }
  }, [filteredAndSortedEvents.length]);

  // Loading state
  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="outlined" onClick={refetch}>
          Retry
        </Button>
      </Box>
    );
  }

  // Event handlers
  const handleOpenEmail = (eventEmailId: string) => {
    EventLogger.getInstance().track({
      name: "click",
      action: "open_mail",
    });
    setOpenEmailId(eventEmailId);
  };

  const handleCloseEmail = () => {
    setOpenEmailId(null);
  };

  const handleDownloadAttachment = async (
    emailId: string,
    attachmentId: string,
    filename?: string,
  ) => {
    try {
      const response = await apiClient.post<
        DownloadAttachmentResponse,
        DownloadAttachmentRequest
      >("/shipments/download-attachment", { emailId, attachmentId });

      const link = document.createElement("a");
      link.href = response.downloadUrl;
      link.download = response.filename || filename || "attachment";
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      EventLogger.getInstance().track({
        name: "click",
        action: "download_attachment",
        attachmentName: response.filename || filename || "<unknown>",
      });
    } catch (err) {
      console.error("Download failed:", err);
    }
  };

  // Handle scroll to email from chat citation
  const handleScrollToEmail = (emailId: string) => {
    // Find the message index by emailId
    let msgIndex = filteredAndSortedEvents.findIndex(
      (event) => event.emailId === emailId
    );

    // If not found in filtered view, check if it exists in unfiltered events
    if (msgIndex < 0) {
      const existsInAll = events.some((event) => event.emailId === emailId);

      if (existsInAll) {
        // Clear all filters to show the email
        setSearchQuery("");
        setShowOnlyWithDocuments(false);
        setSelectedThreadIds(threadInfo.map((t) => t.threadId));

        // Wait for filters to clear and DOM to update, then scroll
        setTimeout(() => {
          // Re-find the message after filters are cleared
          // We need to search in events sorted chronologically (same as filteredAndSortedEvents would be without filters)
          const sortedEvents = [...events].sort((a, b) => {
            if (a.date && b.date) {
              return new Date(a.date).getTime() - new Date(b.date).getTime();
            }
            return 0;
          });
          const newMsgIndex = sortedEvents.findIndex(
            (event) => event.emailId === emailId
          );

          if (newMsgIndex >= 0) {
            scrollToMessageAndHighlight(emailId, newMsgIndex);
          }
        }, 100);
        return;
      }

      // Email not found at all
      console.log("Email not found in timeline:", emailId);
      return;
    }

    scrollToMessageAndHighlight(emailId, msgIndex);
  };

  // Helper to scroll to a message and highlight it
  const scrollToMessageAndHighlight = (emailId: string, msgIndex: number) => {
    // Scroll to the message with extra offset so it's not right at the top
    const msgElement = messageRefs.current.get(`msg-${msgIndex}`);
    if (msgElement && timelineContainerRef.current) {
      const container = timelineContainerRef.current;
      const msgRect = msgElement.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      const targetScroll =
        msgRect.top - containerRect.top + container.scrollTop - 100;

      // Custom fast smooth scroll (2x faster than default)
      const startScroll = container.scrollTop;
      const distance = targetScroll - startScroll;
      const duration = 250; // ms (fast scroll)
      const startTime = performance.now();

      const animateScroll = (currentTime: number) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        // Ease-out curve for smooth deceleration
        const easeOut = 1 - Math.pow(1 - progress, 3);
        container.scrollTop = startScroll + distance * easeOut;

        if (progress < 1) {
          requestAnimationFrame(animateScroll);
        }
      };

      requestAnimationFrame(animateScroll);
    }

    // Highlight the message
    setHighlightedEmailId(emailId);

    // Clear highlight after animation completes (4 seconds)
    setTimeout(() => setHighlightedEmailId(null), 4000);

    // Track the action
    EventLogger.getInstance().track({
      name: "click",
      action: "open_mail",
    });
  };

  // Gray lane X position
  const grayLaneX =
    lanesLeftOffset + laneThreads.length * (laneThickness + laneSpacing);

  return (
    <Box
      className="ShipmentTimelineWithThreadLines"
      sx={{
        pl: 2,
        pt: 0,
        pb: 2,
        pr: 0,
        flex: 1,
        minHeight: 0,
        height: "100%",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Reminders Section - Collapsible */}
      {shipment?.reminders && shipment.reminders.length > 0 && (
        <Box sx={{ pr: 2 }}>
          <Box
            onClick={() => setRemindersExpanded(!remindersExpanded)}
            sx={sectionHeaderSx}
          >
            <ExpandMoreIcon
              sx={{
                fontSize: "1.25rem",
                color: "text.secondary",
                transform: remindersExpanded
                  ? "rotate(0deg)"
                  : "rotate(-90deg)",
                transition: "transform 0.2s ease",
              }}
            />
            <WarningIcon sx={{ fontSize: "1.25rem", color: "#f57c00" }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Reminders ({shipment.reminders.length})
            </Typography>
          </Box>
          <Collapse in={remindersExpanded}>
            <Box sx={{ pt: 1 }}>
              <ShipmentTimelineRemindersComponent
                shipmentId={shipmentId}
                onEmailOpen={handleOpenEmail}
                reminders={shipment.reminders}
              />
            </Box>
          </Collapse>
          <Divider sx={{ mt: 0 }} />
        </Box>
      )}

      {/* Shipment Details - Collapsible */}
      {shipment && (
        <Box sx={{ pr: 2 }}>
          <Box
            onClick={() => setDetailsExpanded(!detailsExpanded)}
            sx={sectionHeaderSx}
          >
            <ExpandMoreIcon
              sx={{
                fontSize: "1.25rem",
                color: "text.secondary",
                transform: detailsExpanded ? "rotate(0deg)" : "rotate(-90deg)",
                transition: "transform 0.2s ease",
              }}
            />
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Shipment Details
            </Typography>
          </Box>
          <Collapse in={detailsExpanded}>
            <Box sx={{ pt: 1 }}>
              <ShipmentDetails
                shipment={shipment}
                updates={updates}
                emailSource={emailSource}
                maxVisibleDetails={10}
                hideTitle
              />
            </Box>
          </Collapse>
          <Divider sx={{ mt: 0 }} />
        </Box>
      )}

      {/* Timeline Section - Collapsible */}
      <Box sx={{ pr: 2 }}>
        <Box
          onClick={() => setTimelineExpanded(!timelineExpanded)}
          sx={sectionHeaderSx}
        >
          <ExpandMoreIcon
            sx={{
              fontSize: "1.25rem",
              color: "text.secondary",
              transform: timelineExpanded ? "rotate(0deg)" : "rotate(-90deg)",
              transition: "transform 0.2s ease",
            }}
          />
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Timeline ({filteredAndSortedEvents.length} emails)
          </Typography>
        </Box>
      </Box>

      {/* Timeline Content - separate container for proper flex behavior */}
      {timelineExpanded && events.length === 0 && (
        <Box sx={{ p: 2, pr: 2 }}>
          <Typography variant="body2" color="text.secondary">
            No events found for this shipment
          </Typography>
        </Box>
      )}

      {/* Filters Row */}
      {timelineExpanded && events.length > 0 && (
        <Box sx={{ pt: 1, pr: 2, flexShrink: 0 }}>
          <TimelineFilters
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            showOnlyWithDocuments={showOnlyWithDocuments}
            onShowOnlyWithDocumentsChange={setShowOnlyWithDocuments}
            threadInfo={threadInfo}
            selectedThreadIds={selectedThreadIds}
            onThreadFilterChange={setSelectedThreadIds}
          />
        </Box>
      )}

      {/* No filtered results */}
      {timelineExpanded &&
        events.length > 0 &&
        searchQuery.trim() &&
        filteredAndSortedEvents.length === 0 && (
          <Box sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary">
              No messages match your search
            </Typography>
          </Box>
        )}

      {/* Timeline with Thread Lines */}
      {timelineExpanded &&
        events.length > 0 &&
        (!searchQuery.trim() || filteredAndSortedEvents.length > 0) && (
          <Box
            className="timeline-container"
            ref={timelineContainerRef}
            sx={{
              position: "relative",
              flex: 1,
              minHeight: 0,
              overflow: "auto",
            }}
          >
            {/* Sticky Header container */}
            <Box
              sx={{
                position: "sticky",
                top: 0,
                zIndex: 10,
                minHeight: 0,
              }}
            >
              <StickyThreadHeader
                stickyLaneThreads={stickyLaneThreads}
                stickyOtherThreadSegment={stickyOtherThreadSegment}
                laneThreads={laneThreads}
                otherThreadSegments={otherThreadSegments}
                laneThickness={laneThickness}
                laneSpacing={laneSpacing}
                lanesLeftOffset={lanesLeftOffset}
                otherThreadsLaneColor={otherThreadsLaneColor}
                dateColumnWidth={dateColumnWidth}
              />
            </Box>

            {/* Content area with lanes and messages */}
            <Box
              className="messages-and-lanes-container"
              sx={{
                position: "relative",
                zIndex: 0,
                minHeight: 200,
                pb: `${bottomPadding}px`,
              }}
            >
              {/* Lane segments - higher z-index to appear above date separator lines */}
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: 2,
                  pointerEvents: "none",
                }}
              >
                <LaneSegments
                  events={filteredAndSortedEvents}
                  messageLanePositions={messageLanePositions}
                  getLaneInfo={getLaneInfo}
                  threadMessageIndices={threadMessageIndices}
                  stickyLaneThreads={stickyLaneThreads}
                  laneThickness={laneThickness}
                  laneSpacing={laneSpacing}
                  lanesLeftOffset={lanesLeftOffset}
                  dashedLineLength={dashedLineLength}
                  otherThreadsLaneColor={otherThreadsLaneColor}
                  laneThreadsCount={laneThreads.length}
                  dateColumnWidth={dateColumnWidth}
                />
              </Box>

              {/* Messages */}
              <Box
                ref={messagesContainerRef}
                sx={{
                  position: "relative",
                }}
              >
                {filteredAndSortedEvents.map((event, index) => {
                  const prevEvent =
                    index > 0 ? filteredAndSortedEvents[index - 1] : null;

                  const showSubject =
                    !prevEvent || prevEvent.threadId !== event.threadId;

                  const currentSender = event.emailFrom?.[0] || "";
                  const prevSender = prevEvent?.emailFrom?.[0] || "";
                  const showSender =
                    currentSender !== prevSender || showSubject;

                  const isSameSenderAsPrevious =
                    currentSender === prevSender && !showSubject;

                  const currentDate = event.date
                    ? new Date(event.date).toDateString()
                    : "";
                  const prevDate = prevEvent?.date
                    ? new Date(prevEvent.date).toDateString()
                    : "";
                  const showDateBullet = currentDate !== prevDate;

                  const laneInfo = getLaneInfo(event.threadId);

                  const threadIndices = event.threadId
                    ? threadMessageIndices.get(event.threadId)
                    : undefined;
                  const isFirstMessageOfLaneThread =
                    threadIndices?.first === index;
                  const isThreadSticky = event.threadId
                    ? stickyLaneThreads.has(event.threadId)
                    : false;
                  const showLaneSubjectInline = !!(
                    laneInfo && isFirstMessageOfLaneThread
                  );
                  const isLaneSubjectHidden = isThreadSticky;

                  // Compute lane info for unified TimelineMessage component
                  const isFirstOfThreadChunk = showSubject && !!prevEvent;
                  let lane: { color: string; xPosition: number };
                  let showSubjectInline: boolean;
                  let isSubjectHidden: boolean;

                  if (laneInfo) {
                    // Colored lane thread
                    lane = {
                      color: laneInfo.color,
                      xPosition:
                        lanesLeftOffset +
                        laneInfo.laneIndex * (laneThickness + laneSpacing),
                    };
                    showSubjectInline = showLaneSubjectInline;
                    isSubjectHidden = isLaneSubjectHidden;
                  } else {
                    // Gray "other threads" lane
                    const segmentIndex = otherThreadSegments.findIndex(
                      (seg) =>
                        seg.firstIndex <= index && index <= seg.lastIndex,
                    );
                    const segment =
                      segmentIndex >= 0
                        ? otherThreadSegments[segmentIndex]
                        : null;
                    const isFirstOfSegment = segment
                      ? segment.firstIndex === index
                      : false;
                    const isSegmentSticky =
                      stickyOtherThreadSegment === segmentIndex;

                    lane = {
                      color: otherThreadsLaneColor,
                      xPosition: grayLaneX,
                    };
                    showSubjectInline = isFirstOfSegment;
                    isSubjectHidden = isSegmentSticky;
                  }

                  return (
                    <TimelineMessage
                      key={event.emailId || `msg-${index}`}
                      event={event}
                      lane={lane}
                      showSubjectInline={showSubjectInline}
                      isSubjectHidden={isSubjectHidden}
                      showSender={showSender}
                      showDateBullet={showDateBullet}
                      isSameSenderAsPrevious={isSameSenderAsPrevious}
                      isFirstOfThreadChunk={isFirstOfThreadChunk}
                      onOpenEmail={handleOpenEmail}
                      onDownloadAttachment={handleDownloadAttachment}
                      laneThickness={laneThickness}
                      bubbleWidth={bubbleWidth}
                      bubbleBaseOffset={bubbleBaseOffset}
                      bubbleReplyOffsetPercent={bubbleReplyOffsetPercent}
                      dateColumnWidth={dateColumnWidth}
                      isHighlighted={highlightedEmailId === event.emailId}
                      refCallback={(el) => {
                        if (el) {
                          messageRefs.current.set(`msg-${index}`, el);
                        }
                      }}
                    />
                  );
                })}
              </Box>

              {/* End of Timeline Footer */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  pt: 4,
                  pb: 10,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                    fontSize: "0.75rem",
                    textAlign: "center",
                  }}
                >
                  End of email exchanges, total {filteredAndSortedEvents.length}{" "}
                  emails.
                  <br />
                  Scroll up for earlier messages, or{" "}
                  <Link
                    component="span"
                    onClick={() => {
                      if (timelineContainerRef.current) {
                        timelineContainerRef.current.scrollTo({
                          top: 0,
                          behavior: "smooth",
                        });
                      }
                    }}
                    sx={{
                      cursor: "pointer",
                      color: "primary.main",
                      textDecoration: "underline",
                    }}
                  >
                    jump to top ↑
                  </Link>
                </Typography>
              </Box>
            </Box>
          </Box>
        )}

      {/* Email Dialog */}
      <EmailDialog emailId={openEmailId} onClose={handleCloseEmail} />

      {/* Debug FAB */}
      <DebugMenu
        shipmentId={shipmentId}
        events={filteredAndSortedEvents}
        isAdmin={user?.isAdmin ?? false}
      />

      {/* Floating Chat Component */}
      <DobbyChatWrapper
        conversationType="shipment_question"
        extraParams={{
          feedId: shipmentId,
        }}
        EmailThreadComponent={EmailThreadComponent}
        onScrollToEmail={handleScrollToEmail}
      />
    </Box>
  );
}
